import { toast } from 'sonner';

import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';

const DEFAULT_CONFIRMATION_MESSAGE = '¿Quieres enviar el mensaje de modificación al cliente?';

type AppointmentModificationConfirmHandler = (message: string) => Promise<boolean>;

type NotificationResponse = {
  channel?: 'whatsapp' | 'email' | 'both';
  emailSent?: boolean;
  waSent?: boolean;
  waStatus?: string;
  waError?: string | null;
  waTemplatesTried?: string[];
};

let confirmHandler: AppointmentModificationConfirmHandler | null = null;

export function registerAppointmentModificationConfirmHandler(
  handler: AppointmentModificationConfirmHandler | null,
): void {
  confirmHandler = handler;
}

export async function requestAppointmentModificationConfirmation(
  confirmationMessage = DEFAULT_CONFIRMATION_MESSAGE,
): Promise<boolean> {
  if (confirmHandler) {
    return confirmHandler(confirmationMessage);
  }

  return window.confirm(confirmationMessage);
}

function buildWhatsAppFailureMessage(result: NotificationResponse): string {
  const reasonMap: Record<string, string> = {
    missing_phone: 'El cliente no tiene un teléfono registrado.',
    empty_message: 'No había contenido para enviar por WhatsApp.',
    missing_template_mapping: 'Falta la configuración de la plantilla de WhatsApp para este tipo de mensaje.',
    send_error: 'Meta rechazó el envío de WhatsApp.',
    text_fallback_failed: 'Meta también rechazó el intento de fallback por texto.',
    notification_failed: 'La integración de WhatsApp respondió con error.',
  };

  const detail = result.waStatus ? reasonMap[result.waStatus] ?? null : null;
  const suffix = result.waError ? ` Detalle: ${result.waError}` : '';
  const templatesInfo = result.waTemplatesTried?.length
    ? ` Plantillas probadas: ${result.waTemplatesTried.join(', ')}.`
    : '';
  return detail
    ? `La cita se guardó y enviamos email, pero WhatsApp no salió. ${detail}${templatesInfo}${suffix}`
    : `La cita se guardó y enviamos email, pero WhatsApp no salió.${templatesInfo}${suffix}`;
}

export async function maybeSendAppointmentModificationNotification(
  appointmentId: string,
  confirmationMessage = DEFAULT_CONFIRMATION_MESSAGE,
): Promise<boolean> {
  const shouldNotifyClient = await requestAppointmentModificationConfirmation(confirmationMessage);
  if (!shouldNotifyClient) return false;

  const { data, error } = await supabase.functions.invoke<NotificationResponse>('send-appointment-email', {
    body: { appointmentId, type: 'modification' },
  });

  if (error) {
    logger.error('appointment_modification_notification_failed', {
      error,
      appointmentId,
    });
    toast.error('La cita se guardó, pero no pudimos enviar el mensaje de modificación.');
    return false;
  }

  if ((data?.channel === 'whatsapp' || data?.channel === 'both') && !data?.waSent) {
    const toastFn = data?.emailSent ? toast.warning : toast.error;
    toastFn(buildWhatsAppFailureMessage(data ?? {}));
  }

  return Boolean(data?.emailSent || data?.waSent);
}
